import { useTranslation } from "next-i18next";
import { Locale, useRouter } from "next/router";

import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { ProductUnknown } from "@/common/contracts/product.contracts";
import {
  buildSoftSoldProductSchema,
  serializeJsonLd,
} from "@/common/utils/json-ld.utils";
import {
  ALTERNATIVES_LEVEL,
  AlternativesLevel,
  CriterionKey,
  PRICE_BAND_OUTCOME,
  PriceBandOutcome,
  SoldAlternativeFilter,
  SoldCategoryFallback,
} from "@/common/utils/sold-alternatives.utils";
import {
  UNAVAILABLE_VARIANT,
  UnavailableVariant,
} from "@/common/utils/sold-product.utils";
import { ProductDetailsBreadcrumbs } from "@/components/ProductDetail/ProductDetailsBreadcrumbs";
import ProductDetailsProvider from "@/components/ProductDetail/ProductDetailsProvider";
import { SoldAlternatives } from "@/components/ProductDetail/SoldAlternatives";
import { SoldItemSummary } from "@/components/ProductDetail/SoldItemSummary";
import { SoldSellerActions } from "@/components/ProductDetail/SoldSellerActions";
import { SoldSimilarItemAlert } from "@/components/ProductDetail/SoldSimilarItemAlert";
import HrefLang from "@/components/common/HrefLang";
import SEO from "@/components/common/seo";
import { CANONICAL_HOST } from "@/config";
import { UsDeliveryQuoteSoldNotice } from "@/features/us-delivery/components/UsDeliveryQuoteSoldNotice";
import {
  type ContentLocale,
  defaultLocale,
  toContentLocale,
} from "@/i18n/config";

/**
 * The page a sold listing becomes, instead of a 301 to a listing page.
 *
 * Served at HTTP 200 for the Soft-Sold and Recently Gone phases, which
 * together cover the first year after a sale; see `sold-product.utils.ts`
 * for the boundaries and `pages/products/[slug].js` for the status codes.
 *
 * ## What this page is for
 *
 * The visitor arrived from Google, an AI answer or an old link wanting one
 * specific object. It is gone, and they have no relationship with Whoppah.
 * So the page has exactly one job: convert that dead end into a sale of
 * something else, or into a standing intent we can satisfy later.
 *
 * ## Structure, after the design review
 *
 * The first version of this page was the live PDP with the buy controls
 * removed. The second inverted it: a small sold strip, the alternatives as
 * the hero, then a trust band, the original item in an accordion, the
 * seller block and the listing-page chips. The design review cut that back
 * again, because the second version still promised the same thing twice
 * ("we will email you" in the alert AND in the app promo inside the trust
 * band), showed the seller's tenure and sales stats twice, and put six
 * sections between the visitor and the one thing worth clicking.
 *
 * What is left is two sections:
 *
 *  1. **Hero.** `SoldItemSummary` on the left states the status as the
 *     page's h1, then shows the item: photo strip, status label, title,
 *     facets, listed price and spec pills. The right rail carries the two
 *     real next actions and nothing else: `SoldSimilarItemAlert` (notify
 *     me) and `SoldSellerActions` (contact the seller, see their other
 *     listings).
 *  2. **`SoldAlternatives`, full width.** The comparable pieces they can
 *     actually have, with one "See more" button under the grid.
 *
 * ## What was removed, and why
 *
 *  - **The USP/trust band.** This page mostly sends people on to other
 *    PDPs, where the same trust content already appears under Add to cart.
 *    It also carried the duplicate seller stat and the app promo. The two
 *    claims that matter at the point of decision survive as the one-line
 *    trust sentence inside `SoldAlternatives`.
 *  - **The "About the piece that sold" accordion.** It held the seller's
 *    full sales pitch for an item nobody can buy. The photo strip and the
 *    spec pills give enough to recognise the piece.
 *  - **The seller block.** Replaced by `SoldSellerActions`, which keeps the
 *    two things a visitor can act on and drops the repeated stats. Its
 *    Contact seller button is also live rather than disabled-with-no-reason.
 *  - **The "Discover more" listing-page chips.** Superseded by the
 *    alternatives grid, which is a more specific path forward.
 *
 * ## What is deliberately NOT here
 *
 *  - **The realised sale price.** The old version of this page printed
 *    "Sold for EUR 220" and put the same number in the JSON-LD offer.
 *    That is per-item transaction data and it is not public information.
 *    The asking price was public while the listing was live, so
 *    `SoldItemSummary` shows that instead, in the past tense. `sold_at` is
 *    additionally redacted out of the props and the Apollo cache by
 *    `redactSoldPrice` in `pages/products/[slug].js`, so it is absent from
 *    `__NEXT_DATA__` too, not merely unrendered.
 *  - **A second similar-items module.** `ProductDetails.tsx` renders
 *    `ProductDetailsSimilarItems`. On a sold page our ladder replaces it,
 *    so it is not rendered here and there is no second module.
 *  - **Bid/buy controls.** `ProductDetailsInfoBidBuy` self-suppresses on
 *    sold items anyway; leaving it out means we never render a disabled
 *    buy button on a page whose whole message is that buying is over.
 *  - **The delivery block.** `info/usp/ProductDetailsDelivery` returns
 *    null for a sold product by design, and quoting shipping options for
 *    an unbuyable item would be a claim we cannot honour. The one trace of
 *    delivery left is `UsDeliveryQuoteSoldNotice` (WHO-6509): on `/en-us`,
 *    a signed-in buyer whose delivery quote this sale closed reads why.
 *
 * The three WHO-5240 variants are untouched: `variant` still decides
 * whether the page may say the item was sold, is reserved, or was simply
 * withdrawn, and `robots` still leaves a reserved listing's indexation
 * alone.
 *
 * The page is `index,follow`. Roughly 37,000 sold listings are in Google's
 * index and they are the entry point for this whole treatment: someone
 * searches for a specific piece, lands here, and is offered buyable
 * alternatives. `noindex` asked Google to throw that away. See
 * `resolveProductAvailability` for the full argument, including what to
 * watch for if these pages start cannibalising live inventory.
 *
 * Everything is rendered from props supplied by `getServerSideProps`, so a
 * crawler that does not execute JavaScript sees the whole page. Note that
 * removing the accordion also removes the full description and the
 * specifications table from that HTML; the spec pills carry a subset. See
 * the PR for the SEO/GEO trade-off this represents.
 */

/** Locale-prefixed PDP path, matching `getProductUrl` in the page. */
const PRODUCT_PATH_BY_LOCALE: Record<string, string> = {
  en: "/products",
  nl: "/nl/producten",
  de: "/de/produkte",
  fr: "/fr/produits",
  es: "/es/productos",
  it: "/it/prodotti",
  // `en-us` is English content under its own prefix (WHO-5766).
  "en-us": "/en-us/products",
};

/**
 * `HrefLang` expects unprefixed, locale-neutral paths and adds the locale
 * itself. Built from the `slug_<locale>` fields the gateway returns on
 * `productBySlug`, so each alternate points at that locale's real slug
 * rather than a machine translation of the English one.
 */
const buildHreflangUrls = (product: ProductBySlugNonNullable) =>
  ({
    en: `/products/${product.slug_en}`,
    nl: `/producten/${product.slug_nl}`,
    de: `/produkte/${product.slug_de}`,
    fr: `/produits/${product.slug_fr}`,
    es: `/productos/${product.slug_es}`,
    it: `/prodotti/${product.slug_it}`,
  }) as Record<ContentLocale, string>;

const buildCanonicalUrl = (
  product: ProductBySlugNonNullable,
  locale: Locale | undefined
) => {
  const resolvedLocale = locale ?? defaultLocale;
  const basePath =
    PRODUCT_PATH_BY_LOCALE[resolvedLocale] ??
    PRODUCT_PATH_BY_LOCALE[defaultLocale];
  const slug =
    product[`slug_${toContentLocale(resolvedLocale)}` as "slug_en"] ??
    product.slug;

  return `${CANONICAL_HOST}${basePath}/${slug}`;
};

/** Provenance of the alternatives, straight off the ladder. */
export type SoftSoldAlternativesMeta = {
  level: AlternativesLevel;
  priceBand: PriceBandOutcome;
  matchCount: number;
  /** Criteria the winning rung actually filtered on. */
  matchedCriteria: CriterionKey[];
  categoryFallback: SoldCategoryFallback | null;
  /** L0 facet set, which is what the "notify me" alert watches. */
  alertFilters: SoldAlternativeFilter[];
  /** Human-readable name for that alert. */
  alertTitle: string;
  /**
   * The sold item's ASKING price, which each card is anchored against.
   * Never its realised price, which is redacted out of this page.
   */
  soldAskingMoney: { amount?: number | null; currency?: string | null } | null;
  /**
   * The sold item's own subcategory with its brand applied, for the "see
   * all N comparable X" link. Computed for every rung, unlike
   * `categoryFallback`, which is only set when the ladder dead-ends.
   */
  browseAll: SoldCategoryFallback | null;
  /** Translated subcategory name, for that link's copy. */
  categoryTitle: string | null;
};

const DEFAULT_ALTERNATIVES_META: SoftSoldAlternativesMeta = {
  level: ALTERNATIVES_LEVEL.L5,
  priceBand: PRICE_BAND_OUTCOME.NotApplicable,
  matchCount: 0,
  matchedCriteria: [],
  categoryFallback: null,
  alertFilters: [],
  alertTitle: "",
  soldAskingMoney: null,
  browseAll: null,
  categoryTitle: null,
};

type SoftSoldProductProps = {
  product: ProductBySlugNonNullable;
  /** Buyable alternatives, already limited server-side by the ladder. */
  similarProducts?: ProductUnknown[];
  alternativesMeta?: SoftSoldAlternativesMeta;
  /**
   * True only when the page was reached through `?preview_sold=1` on a
   * non-production deployment. Adds the 2-vs-4 count toggle so the choice
   * can be made against real pages during review.
   */
  previewMode?: boolean;
  /**
   * Which claim the page is allowed to make (WHO-5240).
   *
   *  - `sold`      the item really sold. Sold badge and date shown.
   *  - `pending`   RESERVED. A sale is in progress but can still fall
   *                through, so the copy says pending, not sold.
   *  - `withdrawn` BANNED, EXPIRED or CANCELED. Nothing was sold, so the
   *                page says only that the item is no longer available.
   *                A "SOLD" badge here would be a false statement.
   */
  variant?: UnavailableVariant;
  /**
   * `robots` content, or null to leave indexation alone.
   *
   * A sold listing is `index,follow`: the indexed sold URL is how a
   * visitor reaches the alternatives at all. A RESERVED one passes null:
   * the reservation releases automatically inside 48 hours, which is
   * shorter than a recrawl, so its index status is left exactly as it
   * was. A withdrawn listing is `noindex,follow`, because nothing was
   * sold and a banned or expired listing should not rank.
   */
  robots?: string | null;
};

export const SoftSoldProduct = ({
  product,
  similarProducts = [],
  alternativesMeta = DEFAULT_ALTERNATIVES_META,
  previewMode = false,
  variant = UNAVAILABLE_VARIANT.Sold,
  robots = "index,follow",
}: SoftSoldProductProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  const resolvedLocale = (locale ?? defaultLocale) as Locale;
  const canonicalUrl = buildCanonicalUrl(product, resolvedLocale);

  // Only the `sold` variant may claim the item was bought. The other
  // two describe items that were never sold, so nothing on the page
  // may state a sale date, a price it fetched, or `SoldOut`.
  const claimsSale = variant === UNAVAILABLE_VARIANT.Sold;

  // A withdrawn or reserved item was not sold, so its meta description
  // may not say so either (WHO-6094). Reuses the page's own headline.
  const description = claimsSale
    ? t("pdp:pdp-soft-sold-description", {
        defaultValue:
          "This item has been sold and is no longer available. Browse similar pieces still for sale on Whoppah.",
      })
    : t("pdp:pdp-sold-strip-headline-withdrawn", {
        defaultValue: "This item is no longer available",
      });

  const schema = buildSoftSoldProductSchema(
    product,
    similarProducts,
    resolvedLocale,
    { claimsSale }
  );

  return (
    <ProductDetailsProvider>
      <SEO
        title={product.title}
        description={description}
        image={product.images?.[0]?.url}
        imageAlt={product.title}
      >
        {/*
          `follow` matters as much as the index directive: the page
          exists to pass authority on to the alternatives and listing
          pages below.

          `robots` is null for a RESERVED item, which leaves indexation
          untouched for the 48 hours before the reservation releases.
        */}
        {robots ? <meta key="robots" name="robots" content={robots} /> : null}
        <link key="canonical" rel="canonical" href={canonicalUrl} />
        <meta key="og:url" property="og:url" content={canonicalUrl} />
      </SEO>

      <HrefLang urlMap={buildHreflangUrls(product)} />

      {schema ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
        />
      ) : null}

      <div className="mx-auto flex w-full max-w-screen-xl flex-col gap-6 px-4 py-2 md:gap-8">
        {/*
          Breadcrumbs from `md` up only. They are the page's only upward
          navigation, and the page passes authority onward, so they are
          worth keeping; at 375px they cost 52px directly above the hero,
          which the fold cannot spare.
        */}
        <div className="hidden md:block">
          <ProductDetailsBreadcrumbs product={product} />
        </div>

        {/*
          The hero: the status and the item on the left, the two real next
          actions in a 320px rail on the right, stacking on a phone. It
          mirrors the live PDP's own shape, which is the point: someone who
          has just come from a PDP finds the actions where they left them.
        */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-8">
          <SoldItemSummary product={product} variant={variant} />

          <div className="flex flex-col gap-4">
            <SoldSimilarItemAlert
              filters={alternativesMeta.alertFilters}
              title={alternativesMeta.alertTitle}
            />
            {/*
              WHO-6509: a US buyer's delivery quote closed by this sale. It appears
              after mount, so it sits below the alert: the rail's first action never
              moves under the visitor's cursor. Not on a withdrawn listing (nothing
              sold); on a reserved one only once the dashboard has closed the quote.
            */}
            {variant !== UNAVAILABLE_VARIANT.Withdrawn && (
              <UsDeliveryQuoteSoldNotice productId={product.id} />
            )}

            <SoldSellerActions product={product} variant={variant} />
          </div>
        </div>

        {/*
          Alternatives, full width, matching the PDP's own layout for this
          pattern. Still the reason the page exists, and now the only thing
          under the hero.
        */}
        <SoldAlternatives
          alternatives={similarProducts}
          level={alternativesMeta.level}
          priceBand={alternativesMeta.priceBand}
          matchCount={alternativesMeta.matchCount}
          matchedCriteria={alternativesMeta.matchedCriteria}
          categoryFallback={alternativesMeta.categoryFallback}
          soldAskingMoney={alternativesMeta.soldAskingMoney}
          browseAll={alternativesMeta.browseAll}
          showCountToggle={previewMode}
          hero
        />
      </div>
    </ProductDetailsProvider>
  );
};

export default SoftSoldProduct;
